/**
 * Craigslist Posting Bridge — Assisted automation via THREE paths:
 *
 * 1. DESKTOP: Chrome extension (FlipFresh) opens CL tab + injects form-filling JS
 * 2. NATIVE MOBILE: Visible WebView (@capgo/inappbrowser) with step-by-step form injection
 * 3. FALLBACK: Copies listing to clipboard + opens CL posting page
 *
 * Key difference from FB bridge: CL requires CAPTCHA + $5 payment,
 * so WebView is VISIBLE and user takes over for final steps.
 * We automate: category selection, all form fields, photo upload.
 * User handles: review, CAPTCHA, payment, publish.
 *
 * Feature: Dealer-only (available during 7-day trial).
 */

import { Capacitor } from '@capacitor/core';
import { api } from './api-client.js';

const EXTENSION_ID = (window as any).__ACP_EXTENSION_ID || 'YOUR_EXTENSION_ID';
declare const chrome: any;

// ── Types ──

export interface CLPostData {
  vehicle_id: string;
  title: string;
  price: number;
  description: string;
  photos: string[];
  posting_type: 'cto' | 'ctd';
  cl_area: string;
  attributes: Record<string, string>;
  location: { city: string; state: string; zip: string } | null;
}

export interface CLPostResult {
  ok: boolean;
  postUrl?: string;
  error?: string;
  method: 'extension' | 'native' | 'assisted';
  step?: string;
}

// ── Helpers ──

function gaussian(mean: number, stddev: number): number {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  return Math.max(mean * 0.3, mean + z * stddev);
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ── Rate limiter ──

const CL_RATE_KEY = 'acp_cl_rate';
function checkCLRate(): { ok: boolean; reason?: string } {
  try {
    const raw = JSON.parse(localStorage.getItem(CL_RATE_KEY) || '[]') as number[];
    const now = Date.now();
    const hourAgo = now - 3600000;
    const dayAgo = now - 86400000;
    const lastHour = raw.filter(t => t > hourAgo);
    const lastDay = raw.filter(t => t > dayAgo);
    if (lastHour.length >= 2) return { ok: false, reason: `Rate limit: ${lastHour.length}/2 this hour. CL is strict — wait a bit.` };
    if (lastDay.length >= 8) return { ok: false, reason: `Rate limit: ${lastDay.length}/8 today. Try again tomorrow.` };
    return { ok: true };
  } catch { return { ok: true }; }
}
function recordCLRate(): void {
  try {
    const raw = JSON.parse(localStorage.getItem(CL_RATE_KEY) || '[]') as number[];
    raw.push(Date.now());
    localStorage.setItem(CL_RATE_KEY, JSON.stringify(raw.slice(-100)));
  } catch { /* best effort */ }
}

// ── Detection ──

type PostMethod = 'extension' | 'native' | 'assisted';

async function detectPostMethod(): Promise<PostMethod> {
  if (typeof chrome !== 'undefined' && chrome?.runtime?.sendMessage) {
    try {
      const ok = await new Promise<boolean>(resolve => {
        chrome.runtime.sendMessage(EXTENSION_ID, { type: 'ACP_PING' }, (r: any) => {
          if (chrome.runtime.lastError) resolve(false);
          else resolve(!!r?.ok);
        });
      });
      if (ok) return 'extension';
    } catch { /* fall through */ }
  }
  if (Capacitor.isNativePlatform()) return 'native';
  return 'assisted';
}

// ═══════════════════════════════════════════════
// PATH 1: DESKTOP — Chrome extension
// ═══════════════════════════════════════════════

async function postCLViaExtension(data: CLPostData): Promise<CLPostResult> {
  return new Promise(resolve => {
    chrome.runtime.sendMessage(EXTENSION_ID, {
      type: 'ACP_CREATE_CL_LISTING',
      data,
    }, (response: any) => {
      if (chrome.runtime.lastError) {
        resolve({ ok: false, error: 'Extension not responding.', method: 'extension' });
        return;
      }
      resolve({ ...(response || { ok: false, error: 'No response' }), method: 'extension' });
    });
  });
}

// ═══════════════════════════════════════════════
// PATH 2: NATIVE MOBILE — Visible WebView
// ═══════════════════════════════════════════════

async function postCLViaNative(data: CLPostData): Promise<CLPostResult> {
  const rate = checkCLRate();
  if (!rate.ok) return { ok: false, error: rate.reason, method: 'native' };

  let IAB: any;
  try {
    IAB = (await import('@capgo/inappbrowser')).InAppBrowser;
  } catch {
    return postCLViaAssisted(data);
  }

  const clUrl = `https://${data.cl_area}.craigslist.org/`;

  try {
    // Open VISIBLE WebView — user must interact for CAPTCHA + payment
    await IAB.openWebView({
      url: clUrl,
      isPresentAfterPageLoad: true,
      preventDeeplink: true,
      title: 'Post to Craigslist',
      toolbarType: 'navigation',
    });

    const result = await orchestrateCLPosting(IAB, data);
    if (result.ok) recordCLRate();
    return result;
  } catch (err: any) {
    try { await IAB.close(); } catch { /* */ }
    return { ok: false, error: err.message || 'Craigslist posting failed', method: 'native' };
  }
}

async function orchestrateCLPosting(IAB: any, data: CLPostData): Promise<CLPostResult> {
  return new Promise(async (resolve) => {
    let resolved = false;
    let currentStep = 'init';
    let detailsFilled = false;
    let photosInjected = false;

    const cleanup = () => {
      resolved = true;
      try { pageListener?.remove?.(); } catch { /* */ }
    };

    // Listen for every page load in the WebView
    const pageListener = await IAB.addListener('browserPageLoaded', async () => {
      if (resolved) return;

      // Small delay for DOM to settle
      await delay(gaussian(800, 200));

      // Get current URL to dispatch to the right step
      let url = '';
      try {
        const urlResult = await IAB.getUrl();
        url = urlResult?.url || '';
      } catch { /* */ }

      const urlLower = url.toLowerCase();

      // Step 6: Success — posting completed
      // DON'T close WebView — user stays in CL to manage their listings
      if (urlLower.includes('/success') || urlLower.includes('/posted')) {
        const postUrl = await extractPostUrl(IAB);
        cleanup();
        // Navigate to their CL account so they can manage postings
        try {
          await IAB.setUrl({ url: `https://accounts.craigslist.org/login/home` });
        } catch { /* best effort — they're already on CL */ }
        resolve({ ok: true, postUrl, method: 'native', step: 'complete' });
        return;
      }

      // Already on manage/account page — user is browsing their listings, stop injecting
      if (urlLower.includes('manage/') || urlLower.includes('accounts.craigslist.org') || urlLower.includes('/login/home')) {
        cleanup();
        return;
      }

      // Step 4: Photo upload page
      if (urlLower.includes('/images') || urlLower.includes('editimage')) {
        if (!photosInjected) {
          photosInjected = true;
          currentStep = 'photos';
          await delay(gaussian(1000, 300));
          await injectPhotoUploader(IAB, data.photos);
        }
        return;
      }

      // Step 3: Details form (the big form page)
      if ((urlLower.includes('/edit') || urlLower.includes('postingForm')) && !detailsFilled) {
        detailsFilled = true;
        currentStep = 'details';
        await delay(gaussian(600, 200));
        await injectDetailsForm(IAB, data);
        return;
      }

      // Step 2: Category selection page
      if (urlLower.match(/\/k\/\w+/) || urlLower.includes('catAbb')) {
        currentStep = 'category';
        await delay(gaussian(500, 150));
        await injectCategorySelection(IAB, data.posting_type);
        return;
      }

      // Step 1: Posting type selection (initial CL page)
      if (urlLower.match(/craigslist\.org\/?(\?.*)?$/) || urlLower.includes('post.craigslist.org')) {
        currentStep = 'type';
        await delay(gaussian(500, 150));
        await injectPostingType(IAB, data.posting_type);
        return;
      }
    });

    // 10-minute timeout — user might be slow with CAPTCHA/payment
    setTimeout(() => {
      if (!resolved) {
        cleanup();
        // Assume user is still working on it — report success optimistically
        resolve({ ok: true, method: 'native', step: currentStep });
      }
    }, 600000);
  });
}

// ── Step Injectors ──

async function injectPostingType(IAB: any, postingType: string): Promise<void> {
  const targetLabel = postingType === 'ctd' ? 'dealer' : 'owner';
  const code = `
    (function() {
      // CL posting type page has radio buttons with labels
      var inputs = document.querySelectorAll('input[type="radio"]');
      var found = false;
      for (var i = 0; i < inputs.length; i++) {
        var parent = inputs[i].closest('label') || inputs[i].parentElement;
        var text = (parent ? parent.textContent : '').toLowerCase();
        if (text.includes('for sale') && text.includes('${targetLabel}')) {
          inputs[i].checked = true;
          inputs[i].click();
          inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
          found = true;
          break;
        }
      }
      // Also try: the page might list categories directly
      if (!found) {
        var links = document.querySelectorAll('a, label');
        for (var j = 0; j < links.length; j++) {
          var lt = links[j].textContent.toLowerCase();
          if (lt.includes('for sale') && lt.includes('${targetLabel}')) {
            links[j].click();
            found = true;
            break;
          }
        }
      }
      // Auto-click continue/submit after brief delay
      if (found) {
        setTimeout(function() {
          var btn = document.querySelector('button.pickbutton') ||
                    document.querySelector('button[type="submit"]') ||
                    document.querySelector('.submit-button') ||
                    document.querySelector('button[name="go"]');
          if (btn) btn.click();
        }, 400);
      }
    })();
  `;
  await IAB.executeScript({ code });
}

async function injectCategorySelection(IAB: any, postingType: string): Promise<void> {
  const code = `
    (function() {
      // Find "cars & trucks" category
      var items = document.querySelectorAll('label, a, span, li');
      var found = false;
      for (var i = 0; i < items.length; i++) {
        var text = items[i].textContent.toLowerCase().trim();
        if (text.includes('cars') && text.includes('truck')) {
          var radio = items[i].querySelector('input[type="radio"]');
          if (radio) {
            radio.checked = true;
            radio.click();
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            items[i].click();
          }
          found = true;
          break;
        }
      }
      // Also try category value match for cto/ctd
      if (!found) {
        var radios = document.querySelectorAll('input[type="radio"]');
        for (var j = 0; j < radios.length; j++) {
          var val = radios[j].value || '';
          if (val === '${postingType === 'ctd' ? 'ctd' : 'cto'}' || val.includes('car')) {
            radios[j].checked = true;
            radios[j].click();
            found = true;
            break;
          }
        }
      }
      if (found) {
        setTimeout(function() {
          var btn = document.querySelector('button.pickbutton') ||
                    document.querySelector('button[type="submit"]') ||
                    document.querySelector('.submit-button');
          if (btn) btn.click();
        }, 400);
      }
    })();
  `;
  await IAB.executeScript({ code });
}

async function injectDetailsForm(IAB: any, data: CLPostData): Promise<void> {
  const attrs = data.attributes;
  // Escape strings for safe JS injection
  const esc = (s: string) => (s || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n').replace(/\r/g, '');

  const code = `
    (function() {
      function setVal(name, value) {
        if (!value && value !== 0) return;
        var el = document.querySelector('[name="' + name + '"]') ||
                 document.getElementById(name);
        if (!el) return;
        if (el.tagName === 'SELECT') {
          var strVal = String(value).toLowerCase();
          // Try exact value match
          for (var i = 0; i < el.options.length; i++) {
            if (el.options[i].value.toLowerCase() === strVal ||
                el.options[i].text.toLowerCase() === strVal) {
              el.selectedIndex = i;
              el.dispatchEvent(new Event('change', { bubbles: true }));
              return;
            }
          }
          // Partial match fallback
          for (var j = 0; j < el.options.length; j++) {
            if (el.options[j].text.toLowerCase().includes(strVal) ||
                el.options[j].value.toLowerCase().includes(strVal)) {
              el.selectedIndex = j;
              el.dispatchEvent(new Event('change', { bubbles: true }));
              return;
            }
          }
        } else if (el.type === 'checkbox') {
          el.checked = !!value;
          el.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          el.value = String(value);
          el.dispatchEvent(new Event('input', { bubbles: true }));
          el.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // Title
      setVal('PostingTitle', '${esc(data.title)}');

      // Price
      setVal('price', '${data.price}');

      // Vehicle attributes (CL field names)
      setVal('condition', '${esc(attrs.condition || '')}');
      setVal('auto_make_model', '${esc(attrs.auto_make_model || '')}');
      setVal('auto_year', '${esc(attrs.auto_year || '')}');
      setVal('auto_miles', '${esc(attrs.auto_miles || '')}');
      setVal('auto_vin', '${esc(attrs.auto_vin || '')}');
      setVal('auto_title_status', '${esc(attrs.auto_title_status || 'clean')}');
      setVal('auto_transmission', '${esc(attrs.auto_transmission || '')}');
      setVal('auto_fuel_type', '${esc(attrs.auto_fuel_type || '')}');
      setVal('auto_paint', '${esc(attrs.auto_paint || '')}');
      setVal('auto_cylinders', '${esc(attrs.auto_cylinders || '')}');
      setVal('auto_drivetrain', '${esc(attrs.auto_drivetrain || '')}');
      setVal('auto_type', '${esc(attrs.auto_type || '')}');
      setVal('auto_size', '${esc(attrs.auto_size || '')}');

      // Description body
      var bodyEl = document.querySelector('[name="PostingBody"]') ||
                   document.getElementById('PostingBody');
      if (bodyEl) {
        bodyEl.value = '${esc(data.description)}';
        bodyEl.dispatchEvent(new Event('input', { bubbles: true }));
      }

      // Location
      setVal('postal', '${esc(data.location?.zip || '')}');
      setVal('city', '${esc(data.location?.city || '')}');
      setVal('region', '${esc(data.location?.city || '')}');
      setVal('GeographicArea', '${esc(data.location?.city || '')}');

      // Signal back
      if (window.mobileApp && window.mobileApp.postMessage) {
        window.mobileApp.postMessage(JSON.stringify({ type: 'cl_step', step: 'details_filled' }));
      }
    })();
  `;
  await IAB.executeScript({ code });
}

async function injectPhotoUploader(IAB: any, photoUrls: string[]): Promise<void> {
  const urlsJson = JSON.stringify(photoUrls.slice(0, 24));

  const code = `
    (async function() {
      var urls = ${urlsJson};
      var fileInput = document.querySelector('input[type="file"]');

      if (!fileInput) {
        // Try to find upload button and click it to reveal file input
        var uploadBtn = document.querySelector('.add-image') ||
                        document.querySelector('[class*="upload"]') ||
                        document.querySelector('button[class*="image"]');
        if (uploadBtn) {
          uploadBtn.click();
          await new Promise(function(r) { setTimeout(r, 1000); });
          fileInput = document.querySelector('input[type="file"]');
        }
      }

      if (!fileInput) {
        if (window.mobileApp && window.mobileApp.postMessage) {
          window.mobileApp.postMessage(JSON.stringify({ type: 'cl_step', step: 'no_file_input' }));
        }
        return;
      }

      // Download all photos as blobs
      var files = [];
      for (var i = 0; i < urls.length; i++) {
        try {
          var res = await fetch(urls[i]);
          if (!res.ok) continue;
          var blob = await res.blob();
          var fileName = 'vehicle_' + (i + 1) + '.jpg';
          var file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
          files.push(file);
        } catch(e) {
          console.log('[ACP-CL] Photo download failed:', i, e.message);
        }
        // Paced downloads — 300ms between each
        if (i < urls.length - 1) {
          await new Promise(function(r) { setTimeout(r, 300); });
        }
      }

      if (files.length === 0) {
        if (window.mobileApp && window.mobileApp.postMessage) {
          window.mobileApp.postMessage(JSON.stringify({ type: 'cl_step', step: 'photos_failed' }));
        }
        return;
      }

      // Use DataTransfer API to set files on the file input
      try {
        var dt = new DataTransfer();
        files.forEach(function(f) { dt.items.add(f); });
        fileInput.files = dt.files;
        fileInput.dispatchEvent(new Event('change', { bubbles: true }));
      } catch(e) {
        // DataTransfer fallback: try drag-and-drop simulation
        try {
          var dropZone = document.querySelector('.dropzone') ||
                         document.querySelector('[class*="drop"]') ||
                         fileInput.parentElement;
          if (dropZone) {
            var dtDrop = new DataTransfer();
            files.forEach(function(f) { dtDrop.items.add(f); });
            var dropEvent = new DragEvent('drop', { dataTransfer: dtDrop, bubbles: true });
            dropZone.dispatchEvent(dropEvent);
          }
        } catch(e2) {
          console.log('[ACP-CL] Drop fallback failed:', e2.message);
        }
      }

      if (window.mobileApp && window.mobileApp.postMessage) {
        window.mobileApp.postMessage(JSON.stringify({
          type: 'cl_step', step: 'photos_uploaded', count: files.length
        }));
      }
    })();
  `;
  await IAB.executeScript({ code });
}

async function extractPostUrl(IAB: any): Promise<string | undefined> {
  const code = `
    (function() {
      var postUrl = null;
      // Look for a link to the posted listing
      var links = document.querySelectorAll('a');
      for (var i = 0; i < links.length; i++) {
        var href = links[i].href || '';
        if (href.match(/craigslist\\.org\\/\\w+\\/d\\//)) {
          postUrl = href;
          break;
        }
      }
      // Check for posting ID in page text
      if (!postUrl) {
        var body = document.body ? (document.body.textContent || '') : '';
        var match = body.match(/posting\\s*(?:id|#)?\\s*:?\\s*(\\d{8,})/i);
        if (match) {
          postUrl = 'https://craigslist.org/posting/' + match[1];
        }
      }
      if (window.mobileApp && window.mobileApp.postMessage) {
        window.mobileApp.postMessage(JSON.stringify({ type: 'cl_complete', postUrl: postUrl }));
      }
    })();
  `;

  return new Promise(async (resolve) => {
    let resolved = false;
    const listener = await IAB.addListener('messageFromWebview', (msg: any) => {
      try {
        const parsed = typeof msg.detail === 'string' ? JSON.parse(msg.detail) : msg.detail;
        if (parsed?.type === 'cl_complete' && !resolved) {
          resolved = true;
          listener?.remove?.();
          resolve(parsed.postUrl || undefined);
        }
      } catch { /* */ }
    });
    await IAB.executeScript({ code });
    setTimeout(() => { if (!resolved) { resolved = true; listener?.remove?.(); resolve(undefined); } }, 10000);
  });
}

// ═══════════════════════════════════════════════
// PATH 3: FALLBACK — Clipboard + open CL page
// ═══════════════════════════════════════════════

async function postCLViaAssisted(data: CLPostData): Promise<CLPostResult> {
  const attrs = data.attributes;
  const lines = [
    data.title,
    '',
    data.description.replace(/<[^>]+>/g, ''),
    '',
    `Price: $${data.price.toLocaleString()}`,
    attrs.auto_vin ? `VIN: ${attrs.auto_vin}` : '',
    attrs.auto_miles ? `Mileage: ${Number(attrs.auto_miles).toLocaleString()} miles` : '',
    '',
    `Year: ${attrs.auto_year}`,
    attrs.auto_make_model ? `Make/Model: ${attrs.auto_make_model}` : '',
    attrs.auto_transmission ? `Transmission: ${attrs.auto_transmission}` : '',
    attrs.auto_fuel_type ? `Fuel: ${attrs.auto_fuel_type}` : '',
    attrs.auto_drivetrain ? `Drivetrain: ${attrs.auto_drivetrain}` : '',
    attrs.auto_paint ? `Color: ${attrs.auto_paint}` : '',
    attrs.auto_type ? `Type: ${attrs.auto_type}` : '',
    attrs.condition ? `Condition: ${attrs.condition}` : '',
  ].filter(Boolean).join('\n').trim();

  try {
    await navigator.clipboard.writeText(lines);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = lines;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }

  window.open(`https://${data.cl_area}.craigslist.org/`, '_blank');

  return { ok: true, method: 'assisted' };
}

// ═══════════════════════════════════════════════
// MAIN ENTRY POINT
// ═══════════════════════════════════════════════

export async function postToCraigslist(vehicleId: string): Promise<CLPostResult> {
  const res = await api.get<CLPostData>(`/vehicles/${vehicleId}/cl-post-data`);
  if (!res.success || !res.data) {
    return { ok: false, error: res.error || 'Failed to load vehicle data', method: 'assisted' };
  }

  const method = await detectPostMethod();

  switch (method) {
    case 'extension':
      return postCLViaExtension(res.data);
    case 'native':
      return postCLViaNative(res.data);
    case 'assisted':
    default:
      return postCLViaAssisted(res.data);
  }
}
